import { isAxiosError } from 'axios';
import { assign, fromPromise, raise, setup } from 'xstate';
import { JobResponse } from '~/types/hlx/jobResponse';
import type { ListingSide } from '../b2bSideCopy';
import { ExistingInquiryError } from '../initialInquiryReceipt';
import { showListingErrorAction } from '../listings/listingConflicts';
import { API_CALLS, JOB_RESPONSE_APIS } from './api';
import { SEARCH_FRESH_MS } from './consts';
import {
  isUncertainListingActionError,
  sendListingActionError,
  shouldKeepMessageEditorOpen,
} from './errors';
import {
  Context,
  DoneApplyEvent,
  DoneGetEvent,
  Events,
  MsaasJobListingResponse,
  OnCheckFitEvent,
  OnCheckFitFailedEvent,
  OnConfirmMessageEvent,
  OnNegotiationSubmittedEvent,
  OnPaginateEvent,
  OnRouteEnterEvent,
  OnSearchEvent,
  OnSelectEvent,
  OnSortEvent,
  Scope,
  SortBy,
} from './types';

// Best match is the Buying tab's default (DD11); the Selling tab keeps today's.
export const defaultSortFor = (side: ListingSide): SortBy =>
  side === 'OFFERING' ? 'BEST_MATCH' : 'MOST_RECENT';

const FIT_QUOTA_RESET_TIMER = 'fitQuotaReset';

const nextUtcMidnight = () => {
  const now = new Date();
  return Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1);
};

// The machine lifts the lock itself at the reset (ON_FIT_QUOTA_RESET), so a held time is a live lock.
const isFitQuotaActive = (context: Context) => context.fitQuotaResetAt != null;

const isSearchFresh = (context: Context) =>
  context.loadedAt != null && Date.now() - context.loadedAt < SEARCH_FRESH_MS;

const employerChanged = (context: Context, employerUser: { employerId?: number } | undefined) =>
  !!employerUser?.employerId && context.employerUser?.employerId !== employerUser.employerId;

// Apply a JobResponse mutation to both the cached list row and the selected
// listing so they stay in sync. Without this, re-selecting a listing rehydrates
// from stale data in `listings` and CTAs derived from `listing.jobResponses`
// (Apply / Negotiate / Message vs. "already responded") behave incorrectly.
const applyJobResponseChange = (
  context: Context,
  listingId: number | undefined,
  mutate: (listing: MsaasJobListingResponse) => MsaasJobListingResponse
): Pick<Context, 'listing' | 'listings'> => {
  if (listingId === undefined) {
    return { listing: context.listing, listings: context.listings };
  }
  const listings = context.listings.map((row) => (row.id === listingId ? mutate(row) : row));
  const listing = context.listing?.id === listingId ? mutate(context.listing) : context.listing;
  return { listing, listings };
};

const getJobResponseFromMutationEvent = (
  event: DoneApplyEvent | OnNegotiationSubmittedEvent
): JobResponse => {
  return 'output' in event ? event.output : event.payload.jobResponse;
};

const routeEntryReset = (
  context: Context,
  employerUser: OnRouteEnterEvent['payload']['employerUser']
): Partial<Context> => ({
  listings: [],
  listing: undefined,
  employerUser: employerUser?.employerId ? employerUser : context.employerUser,
  tabIndex: 0,
  pagination: {
    page: 1,
    pageSize: context.pagination.pageSize,
    total: 0,
  },
  filters: {},
  sortBy: defaultSortFor(context.listingSide),
  loadedAt: undefined,
  modalOpen: false,
  closeModalAfterSend: false,
  routeEntryPending: false,
  deferredRouteEntryEmployerUser: undefined,
  messageDraft: '',
  existingInquiryResponse: undefined,
  actionAttempt:
    context.actionUncertain &&
    context.actionAttempt?.employerId ===
      (employerUser?.employerId ?? context.employerUser?.employerId)
      ? context.actionAttempt
      : undefined,
  actionUncertain:
    !!context.actionUncertain &&
    context.actionAttempt?.employerId ===
      (employerUser?.employerId ?? context.employerUser?.employerId),
});

export const listingsSearchMachine = setup({
  types: {
    context: {} as Context,
    events: {} as Events,
    input: {} as Partial<Context>,
  },
  actors: {
    getListings: fromPromise(API_CALLS.get),
    apply: fromPromise(JOB_RESPONSE_APIS.apply),
    message: fromPromise(JOB_RESPONSE_APIS.message),
  },
  actions: {
    setListings: assign({
      loadedAt: () => Date.now(),
      listings: ({ event }: Scope) => (event as DoneGetEvent).output.data,
      listing: ({ event, context }: Scope) => {
        const listings = (event as DoneGetEvent).output.data;
        const listingId =
          context.listing?.id ??
          (context.actionUncertain ? context.actionAttempt?.listingId : undefined);
        return listings.find((listing) => listing.id === listingId) ?? listings[0];
      },
      pagination: ({ event }: Scope) => {
        const { page, pageSize, total } = (event as DoneGetEvent).output;
        return { page, pageSize, total };
      },
    }),
    setJobResponse: assign(({ event, context }: Scope) => {
      const jobResponse = getJobResponseFromMutationEvent(
        event as DoneApplyEvent | OnNegotiationSubmittedEvent
      );
      // Prefer the response's own msaasListingId so a late-resolving POST
      // (user closed modal mid-flight, then selected a different listing)
      // attaches to the originating listing instead of the now-selected one.
      const targetListingId =
        jobResponse.msaasListingId ?? context.actionAttempt?.listingId ?? context.listing?.id;
      return applyJobResponseChange(context, targetListingId, (row) => {
        const jobResponses = Array.isArray(row.jobResponses) ? row.jobResponses : [];
        const existingIndex = jobResponses.findIndex((existing) => existing.id === jobResponse.id);
        return {
          ...row,
          jobResponses:
            existingIndex === -1
              ? [...jobResponses, jobResponse]
              : jobResponses.map((existing) =>
                  existing.id === jobResponse.id ? jobResponse : existing
                ),
        };
      });
    }),
    setLoading: assign({
      loading: true,
    }),
    resetLoading: assign({
      loading: false,
    }),
    resetSelectedListing: assign({
      listing: ({ context }: Scope) => {
        return context.listings.length > 0 ? context.listings[0] : undefined;
      },
    }),
    setPagination: assign({
      pagination: ({ event, context }: Scope) => {
        return {
          ...context.pagination,
          page: (event as OnPaginateEvent).page,
        };
      },
    }),
    resetPagination: assign({
      pagination: ({ context }: Scope) => {
        return {
          ...context.pagination,
          page: 1,
        };
      },
    }),
    // The Buying search and its fit checks can be refused with a code (FEATURE_DISABLED,
    // COMPARE_LISTING_FORBIDDEN, FIT_QUOTA_EXCEEDED, NO_ROSTER); those are spelled out, the rest is
    // the shared alert.
    sendError: showListingErrorAction,
    // "Check fit" shows the row as pending straight away; the next read confirms or corrects it.
    markFitPending: assign(({ context, event }: Scope) => {
      const { listingId } = (event as OnCheckFitEvent).payload;
      return applyJobResponseChange(context, listingId, (row) =>
        row.fit ? { ...row, fit: { ...row.fit, status: 'PENDING' } } : row
      );
    }),
    // Runs before markFitPending, so the row still has the status a failure has to put back.
    runFitCheck: ({
      context,
      event,
      self,
    }: Scope & { self: { send: (event: Events) => void } }) => {
      const { listingId } = (event as OnCheckFitEvent).payload;
      const seekingListingId = context.filters.compareListing?.value;
      if (seekingListingId == null) return;
      const previousStatus = context.listings.find((row) => row.id === listingId)?.fit?.status;
      API_CALLS.checkFit({ offeringListingId: listingId, seekingListingId })
        .then((result) => {
          // Anything but PENDING means nothing was queued (already scored, roster too small, ...),
          // so read the real state now instead of waiting on a poll that is not coming.
          if (result?.status && result.status !== 'PENDING') self.send({ type: 'ON_REFRESH' });
        })
        .catch((error) =>
          self.send({
            type: 'ON_CHECK_FIT_FAILED',
            listingId,
            compareListingId: seekingListingId,
            quotaExceeded: isAxiosError(error) && error.response?.status === 429,
            previousStatus,
            error,
          })
        );
    },
    revertFitPending: assign(({ context, event }: Scope) => {
      const { listingId, previousStatus } = event as OnCheckFitFailedEvent;
      return applyJobResponseChange(context, listingId, (row) =>
        row.fit ? { ...row, fit: { ...row.fit, status: previousStatus ?? 'NOT_SCORED' } } : row
      );
    }),
    setFitQuotaExceeded: assign({ fitQuotaResetAt: () => nextUtcMidnight() }),
    // Lifts the lock at the reset, so the buttons come back without any other event.
    scheduleFitQuotaReset: raise(
      { type: 'ON_FIT_QUOTA_RESET' },
      { id: FIT_QUOTA_RESET_TIMER, delay: () => Math.max(0, nextUtcMidnight() - Date.now()) }
    ),
    clearFitQuota: assign({ fitQuotaResetAt: undefined }),
    sendActionError: sendListingActionError,
    saveActionAttempt: assign(({ context, event }) => {
      if (
        (event.type !== 'ON_CONFIRM_APPLY' && event.type !== 'ON_CONFIRM_MESSAGE') ||
        !context.listing ||
        !context.employerUser?.employerId
      )
        return { actionAttempt: undefined, actionUncertain: false };
      return {
        actionUncertain: false,
        actionAttempt: {
          kind: event.type === 'ON_CONFIRM_APPLY' ? ('apply' as const) : ('message' as const),
          listingId: context.listing.id,
          listingTitle: context.listing.listingTitle,
          employerId: context.employerUser.employerId,
          providerIds: [...(event.payload?.providerIds ?? [])],
          message: event.type === 'ON_CONFIRM_MESSAGE' ? event.payload.message : undefined,
        },
      };
    }),
    markActionUncertain: assign({
      actionUncertain: ({ event }) =>
        'error' in event && isUncertainListingActionError(event.error),
    }),
    clearActionAttempt: assign({ actionAttempt: undefined, actionUncertain: false }),
    reconcileActionAttempt: assign(({ context }) => {
      const attempt = context.actionAttempt;
      if (!attempt) return {};
      const response = context.listings
        .find((row) => row.id === attempt.listingId)
        ?.jobResponses?.find((row) => row.respondingEmployerId === attempt.employerId);
      const completed =
        attempt.kind === 'message'
          ? (response?.messageCount ?? 0) > 0
          : (response?.negotiationCount ?? 0) > 0 || response?.initialNegotiationId != null;
      return completed ? { actionAttempt: undefined, actionUncertain: false } : {};
    }),
    setSort: assign({
      sortBy: ({ event }: Scope) => (event as OnSortEvent).payload.sortBy,
    }),
    setFilters: assign({
      filters: ({ event }: Scope) => (event as OnSearchEvent).payload.filters,
    }),
    openModal: assign({
      modalOpen: true,
    }),
    closeModal: assign({
      modalOpen: false,
    }),
    setMessageDraft: assign({
      messageDraft: ({ event }: Scope) => (event as OnConfirmMessageEvent).payload.message,
    }),
    clearMessageDraft: assign({
      messageDraft: '',
      existingInquiryResponse: undefined,
    }),
    retainExistingInquiry: assign(({ event }) => ({
      existingInquiryResponse:
        'error' in event && event.error instanceof ExistingInquiryError
          ? event.error.jobResponse
          : undefined,
    })),
    resetForRouteEntry: assign(({ event, context }: Scope) =>
      routeEntryReset(context, (event as OnRouteEnterEvent).payload.employerUser)
    ),
    deferRouteEntry: assign(({ event }: Scope) => ({
      routeEntryPending: true,
      deferredRouteEntryEmployerUser: (event as OnRouteEnterEvent).payload.employerUser,
    })),
    resetForDeferredRouteEntry: assign(({ context }: Scope) =>
      routeEntryReset(context, context.deferredRouteEntryEmployerUser)
    ),
    // A deferral is only valid for the invoke that was in flight when
    // ON_ROUTE_ENTER arrived. Every root-level transition (ON_SEARCH, ON_SORT,
    // ON_PAGINATE, ON_APPLY, ON_NEGOTIATE, ON_MESSAGE, ON_LOAD) exits the
    // invoking state and xstate discards its onDone, so nothing would ever
    // consume the flag and the NEXT mutation would wipe listings, filters,
    // sort, page and selection against a stale deferredRouteEntryEmployerUser.
    // Cleared on entry to $_VIEW and to the states a stale flag can reach
    // WITHOUT passing through $_VIEW:
    //   $_SEND_APPLY   via ON_APPLY -> $_APPLY (no entry actions)
    //   $_SEND_MESSAGE via ON_MESSAGE -> $_MESSAGE
    // [MCV-1646] ON_NEGOTIATE no longer invokes anything itself -- it targets
    // $_NEGOTIATE directly, which has no side effect to defer -- so it is no
    // longer one of the states this comment enumerates.
    // Each remaining one has a test driving that exact hop. Entry runs after
    // the entering transition's actions (so resetForDeferredRouteEntry still
    // reads the deferred employerUser) and before any event is processed, and
    // deferRouteEntry only fires from the targetless -- therefore internal,
    // non-re-entering -- ON_ROUTE_ENTER handlers below, so this can never erase
    // a live deferral.
    clearRouteEntry: assign({
      routeEntryPending: false,
      deferredRouteEntryEmployerUser: undefined,
    }),
  },
}).createMachine({
  context: ({ input }) => ({
    listingSide: input?.listingSide ?? 'SEEKING',
    listings: [],
    listing: undefined,
    employerUser: undefined,
    tabIndex: 0,
    loading: false,
    pagination: {
      page: 1,
      pageSize: 20,
      total: 0,
    },
    filters: {},
    sortBy: defaultSortFor(input?.listingSide ?? 'SEEKING'),
    modalOpen: false,
    closeModalAfterSend: false,
    routeEntryPending: false,
    deferredRouteEntryEmployerUser: undefined,
    messageDraft: '',
  }),
  initial: '$_VIEW',
  on: {
    ON_ROUTE_ENTER: [
      {
        // The Buying tab loads when it is shown (ON_ACTIVATE), not on every route entry: most
        // visits never open it.
        guard: ({ context }) => context.listingSide === 'OFFERING',
        actions: ['resetForRouteEntry', 'resetLoading'],
      },
      {
        // Route entry should still clear stale search state if identity has not
        // resolved, but it cannot issue a correctly-scoped request yet.
        guard: ({ event, context }) =>
          !event?.payload?.employerUser?.employerId && !context.employerUser?.employerId,
        actions: ['resetForRouteEntry', 'resetLoading'],
      },
      {
        target: '.$_LOAD',
        actions: ['resetForRouteEntry', 'setLoading'],
      },
    ],
    ON_LOAD: [
      {
        guard: ({ context, event }) =>
          !!event.payload?.employerUser?.employerId &&
          context.employerUser?.employerId !== event.payload.employerUser.employerId,
        target: '.$_LOAD',
        actions: ['resetForRouteEntry', 'setLoading'],
      },
      {
        // Defensive: when ON_LOAD fires without a resolved employerUser (e.g.
        // marketState dispatched before $_LOAD_EMPLOYER finished), keep the
        // existing employerUser intact and skip the network call. Otherwise
        // the search can load before we know which employer's existing
        // responses should drive the listing CTAs.
        guard: ({ event, context }) =>
          !event?.payload?.employerUser?.employerId && !context.employerUser?.employerId,
        actions: ['resetLoading'],
      },
      {
        target: '.$_LOAD',
        actions: [
          assign({
            // Mirror the guard above: only adopt the incoming employerUser when
            // it has a real employerId, otherwise a partially-populated payload
            // (e.g. user object loaded but employerId still pending) would
            // clobber the cached one and response-derived CTAs would lose the
            // viewer identity.
            employerUser: ({ event, context }) =>
              event?.payload?.employerUser?.employerId
                ? event.payload.employerUser
                : context.employerUser,
          }),
          'setLoading',
        ],
      },
    ],
    ON_SET_EMPLOYER_USER: [
      {
        guard: ({ context, event }) =>
          context.listingSide === 'OFFERING' &&
          employerChanged(context, event.payload.employerUser),
        actions: ['resetForRouteEntry', 'resetLoading'],
      },
      {
        guard: ({ context, event }) => employerChanged(context, event.payload.employerUser),
        target: '.$_LOAD',
        actions: ['resetForRouteEntry', 'setLoading'],
      },
      {
        actions: assign({
          employerUser: ({ event, context }) =>
            event?.payload?.employerUser?.employerId
              ? event.payload.employerUser
              : context.employerUser,
        }),
      },
    ],
    // [MCV-1889] The Buying tab was shown: load it, unless fresh results are already held.
    ON_ACTIVATE: [
      {
        guard: ({ context }) =>
          !context.employerUser?.employerId ||
          context.loading ||
          context.modalOpen ||
          isSearchFresh(context),
      },
      { target: '.$_LOAD', actions: ['setLoading'] },
    ],
    ON_CHECK_FIT: {
      guard: ({ context }) =>
        context.filters.compareListing?.value != null && !isFitQuotaActive(context),
      actions: ['runFitCheck', 'markFitPending'],
    },
    ON_CHECK_FIT_FAILED: [
      {
        // A check for a buyer listing that is no longer the one compared: the search has moved on to
        // other results, so its failure must not put a row of the current ones back.
        guard: ({ context, event }) =>
          event.compareListingId !== context.filters.compareListing?.value,
      },
      {
        // 429 FIT_QUOTA_EXCEEDED: say so once with the server's own wording; the buttons stay off.
        guard: ({ event }) => event.quotaExceeded,
        actions: ['revertFitPending', 'setFitQuotaExceeded', 'scheduleFitQuotaReset', 'sendError'],
      },
      { actions: ['revertFitPending', 'sendError'] },
    ],
    ON_FIT_QUOTA_RESET: { actions: 'clearFitQuota' },
    ON_SET_TAB_INDEX: {
      actions: assign({
        tabIndex: ({ event }) => event?.payload?.index,
      }),
    },
    ON_SELECT: {
      actions: [
        assign(({ event, context }: Scope) => {
          const { listingId } = (event as OnSelectEvent).payload;
          return {
            listing: context.listings.find((listing) => listing.id === listingId),
          };
        }),
      ],
    },
    ON_SEARCH: {
      target: '.$_LOAD',
      actions: ['setLoading', 'resetPagination', 'setFilters'],
    },
    ON_PAGINATE: {
      target: '.$_LOAD',
      actions: ['setPagination', 'setLoading'],
    },
    ON_SORT: {
      target: '.$_LOAD',
      actions: ['setSort', 'setLoading', 'resetPagination'],
    },
    ON_APPLY: {
      guard: ({ context }) => !context.actionUncertain || !context.actionAttempt,
      actions: ['openModal'],
      target: '.$_APPLY',
    },
    // [MCV-1646] Draft-first initial offer: opening "Negotiate" no longer
    // creates anything server-side. It just opens the modal into $_NEGOTIATE,
    // where NegotiationDetails itself loads a draft scoped to the selected
    // listing and only a Submit Offer click inside it creates the JobResponse
    // (see ON_NEGOTIATION_SUBMITTED below, handled from $_NEGOTIATE).
    ON_NEGOTIATE: {
      guard: ({ context }) => !context.actionUncertain || !context.actionAttempt,
      actions: ['openModal'],
      target: '.$_NEGOTIATE',
    },
    ON_MESSAGE: {
      guard: ({ context }) => !context.actionUncertain || !context.actionAttempt,
      actions: ['clearMessageDraft', 'openModal'],
      target: '.$_MESSAGE',
    },
    ON_CLOSE_MODAL: {
      actions: ['closeModal'],
      target: '.$_VIEW',
    },
    // [MCV-1646] Cache the JobResponse the negotiation editor's draft-submit
    // just created/reused onto the originating listing row.
    // [MCV-1816] The first offer is sent to the other party, so the modal now
    // closes (the listing row shows the response). It used to stay open on
    // "Awaiting Other Party".
    ON_NEGOTIATION_SUBMITTED: {
      actions: ['setJobResponse', 'closeModal'],
      target: '.$_VIEW',
    },
  },
  states: {
    $_VIEW: {
      entry: ['closeModal', 'resetLoading', 'clearMessageDraft', 'clearRouteEntry'],
      on: {
        // A quiet re-read (no spinner, selection kept) while fit scores are still pending.
        ON_REFRESH: { target: '$_REFRESH' },
        ON_DISMISS_ACTION_RETRY: {
          guard: ({ context }) => !!context.actionUncertain,
          actions: ['clearActionAttempt'],
        },
        ON_RETRY_ACTION: [
          {
            guard: ({ context }) =>
              !!context.actionUncertain &&
              context.actionAttempt?.kind === 'apply' &&
              context.actionAttempt.employerId === context.employerUser?.employerId,
            target: '$_SEND_APPLY',
            actions: ['setLoading'],
          },
          {
            guard: ({ context }) =>
              !!context.actionUncertain &&
              context.actionAttempt?.kind === 'message' &&
              context.actionAttempt.employerId === context.employerUser?.employerId,
            target: '$_SEND_MESSAGE',
            actions: ['setLoading'],
          },
        ],
      },
    },
    $_LOAD: {
      invoke: {
        src: 'getListings',
        input: ({ context, event }: Scope) => ({ context, event }),
        onDone: {
          target: '$_VIEW',
          actions: ['setListings', 'reconcileActionAttempt'],
        },
        onError: {
          target: '$_VIEW',
          actions: ['sendError'],
        },
      },
    },
    // A polling read: same as $_LOAD, but a failure is silent (the next poll tries again) instead of
    // raising an alert each time.
    $_REFRESH: {
      invoke: {
        src: 'getListings',
        input: ({ context, event }: Scope) => ({ context, event }),
        onDone: {
          target: '$_VIEW',
          actions: ['setListings', 'reconcileActionAttempt'],
        },
        onError: { target: '$_VIEW' },
      },
    },
    $_APPLY: {
      on: {
        ON_CONFIRM_APPLY: {
          target: '$_SEND_APPLY',
          actions: ['saveActionAttempt', 'setLoading'],
        },
      },
    },
    // [MCV-1646] No invoke here at all -- ActionModal renders NegotiationDetails
    // on a draft source, and everything up to and including the first Submit
    // Offer happens inside the negotiation editor's own machine. This state
    // only exists to render the modal body; ON_CLOSE_MODAL and
    // ON_NEGOTIATION_SUBMITTED are handled at the root since there is nothing
    // in flight here that a state exit could discard.
    $_NEGOTIATE: {},
    $_MESSAGE: {
      on: {
        ON_CONFIRM_MESSAGE: {
          guard: ({ context }) => !context.existingInquiryResponse,
          target: '$_SEND_MESSAGE',
          actions: ['saveActionAttempt', 'setMessageDraft', 'setLoading'],
        },
      },
    },
    $_SEND_APPLY: {
      entry: ['clearRouteEntry'],
      on: {
        // State-local handler shadows the root ON_CLOSE_MODAL so XState does
        // not exit mid-flight and discard onDone. We close the modal UI
        // immediately (closeModal) so the visual dismissal feels responsive,
        // but we stay in this state so the POST's onDone can still run
        // setJobResponse against the resolved payload. closeModalAfterSend
        // is a no-op today (closeModal already ran) but kept for symmetry
        // with $_START_NEGOTIATE and as an audit trail of intent.
        ON_CLOSE_MODAL: {
          actions: ['closeModal', assign({ closeModalAfterSend: true })],
        },
        ON_ROUTE_ENTER: [
          {
            guard: ({ context, event }) =>
              !!event.payload.employerUser?.employerId &&
              context.employerUser?.employerId !== event.payload.employerUser.employerId,
            target: '$_LOAD',
            actions: ['resetForRouteEntry', 'setLoading'],
          },
          { actions: ['deferRouteEntry', 'closeModal', 'setLoading'] },
        ],
      },
      invoke: {
        src: 'apply',
        input: ({ context, event }: Scope) => ({ context, event }),
        onDone: [
          {
            guard: ({ context }) => context.routeEntryPending,
            target: '$_LOAD',
            actions: [
              'setJobResponse',
              'clearActionAttempt',
              'resetForDeferredRouteEntry',
              'setLoading',
            ],
          },
          {
            target: '$_VIEW',
            // $_VIEW's entry already runs closeModal, so the visual modal
            // will hide regardless. We only need to run setJobResponse so
            // the applied response gets attached to the client-side listing,
            // and reset the flag for the next cycle.
            actions: [
              'setJobResponse',
              'clearActionAttempt',
              assign({ closeModalAfterSend: false }),
            ],
          },
        ],
        onError: [
          {
            guard: ({ context }) => context.routeEntryPending,
            target: '$_LOAD',
            actions: [
              'markActionUncertain',
              'sendActionError',
              'resetForDeferredRouteEntry',
              'setLoading',
            ],
          },
          {
            target: '$_LOAD',
            actions: [
              'markActionUncertain',
              'sendActionError',
              'setLoading',
              assign({ closeModalAfterSend: false }),
            ],
          },
        ],
      },
    },
    $_SEND_MESSAGE: {
      entry: ['clearRouteEntry'],
      on: {
        // Same race mitigation as $_SEND_APPLY: preserve setJobResponse on
        // the resolved payload even when the user has dismissed the modal
        // before the POST completed.
        ON_CLOSE_MODAL: {
          actions: ['closeModal', assign({ closeModalAfterSend: true })],
        },
        ON_ROUTE_ENTER: [
          {
            guard: ({ context, event }) =>
              !!event.payload.employerUser?.employerId &&
              context.employerUser?.employerId !== event.payload.employerUser.employerId,
            target: '$_LOAD',
            actions: ['resetForRouteEntry', 'setLoading'],
          },
          { actions: ['deferRouteEntry', 'closeModal', 'setLoading'] },
        ],
      },
      invoke: {
        src: 'message',
        input: ({ context, event }: Scope) => ({ context, event }),
        onDone: [
          {
            guard: ({ context }) => context.routeEntryPending,
            target: '$_LOAD',
            actions: [
              'setJobResponse',
              'clearActionAttempt',
              'resetForDeferredRouteEntry',
              'setLoading',
            ],
          },
          {
            target: '$_VIEW',
            actions: [
              'setJobResponse',
              'clearActionAttempt',
              assign({ closeModalAfterSend: false }),
            ],
          },
        ],
        onError: [
          {
            guard: ({ context }) => context.routeEntryPending,
            target: '$_LOAD',
            actions: [
              'markActionUncertain',
              'sendActionError',
              'resetForDeferredRouteEntry',
              'setLoading',
            ],
          },
          {
            guard: ({ event }) => shouldKeepMessageEditorOpen(event),
            target: '$_MESSAGE',
            actions: [
              'retainExistingInquiry',
              'clearActionAttempt',
              'sendActionError',
              'resetLoading',
              assign({ closeModalAfterSend: false }),
            ],
          },
          {
            target: '$_LOAD',
            actions: [
              'markActionUncertain',
              'sendActionError',
              'setLoading',
              assign({ closeModalAfterSend: false }),
            ],
          },
        ],
      },
    },
  },
});
